import React, { useState } from 'react';
import { X, User, Mail } from 'lucide-react';
import { Button } from '../ui/Button';
import { AttendanceChart } from './AttendanceChart';
import { useStudentAttendanceHistory } from '../../hooks/useStudentAttendanceHistory';
import { TopStudent } from '../../hooks/useTopStudents';
import { useCompanySports } from '../../hooks/useCompanySports';

interface StudentDetailModalProps {
  student: TopStudent;
  onClose: () => void;
  onViewFullProfile?: () => void;
}

export function StudentDetailModal({ student, onClose, onViewFullProfile }: StudentDetailModalProps) {
  const [timePeriod, setTimePeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [sportFilter, setSportFilter] = useState<string | null>(null);
  const { data: sports } = useCompanySports();
  const { data: attendanceHistory, isLoading: historyLoading } = useStudentAttendanceHistory(
    student.student_id,
    timePeriod,
    sportFilter
  );

  const studentName = `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown Student';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#221F1F] border-b border-[#504A4A] p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {student.avatar_url ? (
              <img
                src={student.avatar_url}
                alt={studentName}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#9B1421] flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-text-primary">{studentName}</h2>
              {student.email && (
                <div className="flex items-center gap-2 text-text-secondary text-sm">
                  <Mail className="w-4 h-4" />
                  {student.email}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Chart Controls */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-text-secondary">Period:</label>
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value as 'week' | 'month' | 'quarter' | 'year')}
                className="bg-[#363232] border border-[#504A4A] text-text-primary rounded px-3 py-1 text-sm"
              >
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-text-secondary">Sport:</label>
              <select
                value={sportFilter || ''}
                onChange={(e) => setSportFilter(e.target.value || null)}
                className="bg-[#363232] border border-[#504A4A] text-text-primary rounded px-3 py-1 text-sm"
              >
                <option value="">All Sports</option>
                {sports?.map((sport) => (
                  <option key={sport.id} value={sport.id}>
                    {sport.sport_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Attendance Chart */}
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
            </div>
          ) : (
            <AttendanceChart
              data={attendanceHistory || []}
            />
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            {onViewFullProfile && (
              <Button
                onClick={onViewFullProfile}
                className="bg-[#363232] hover:bg-[#504A4A] text-text-primary"
              >
                View Full Profile
              </Button>
            )}
            <Button
              onClick={onClose}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
